import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnDestroy, Signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { format, isSameDay } from 'date-fns';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { ButtonComponent } from 'ui-library';
import { catchError, first, forkJoin, map, of, Subject, takeUntil } from 'rxjs';
import { ReportService } from 'src/app/services/api/reports/report.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { Project } from 'src/app/interfaces/api/project';
import { Pop } from 'src/app/classes/shared/pop';
import { TrackerConfigTool } from 'src/app/classes/shared/tracker-config.tool';
import { formatDistanceMeters } from 'src/app/classes/shared/decimal-format.tool';
import { DeleteModalComponent } from 'src/app/components/shared/dialogs/delete-modal/delete-modal.component';
import { TemplatePickerDialogComponent } from 'src/app/components/shared/dialogs/template-picker-dialog/template-picker-dialog.component';
import { KydConfigDialogComponent } from './kyd-config-dialog/kyd-config-dialog.component';
import { KydBulkAddDialogComponent, KydBulkAddResult } from './kyd-bulk-add-dialog/kyd-bulk-add-dialog.component';
import { KydBulkConfigDialogComponent } from './kyd-bulk-config-dialog/kyd-bulk-config-dialog.component';
import { KydConfigureChoiceDialogComponent } from './kyd-configure-choice-dialog/kyd-configure-choice-dialog.component';
import { ReportDetailDialogComponent } from './report-detail-dialog/report-detail-dialog.component';

@Component({
    selector: 'app-kyd',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        MatToolbarModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatSelectModule,
        MatTableModule,
        MatPaginatorModule,
        MatIconModule,
        MatDialogModule,
        MatCardModule,
        MatCheckboxModule,
        MatTabsModule,
        NoDataComponent,
        ButtonComponent
    ],
    templateUrl: './kyd.component.html',
    styleUrl: './kyd.component.scss',
    standalone: true
})

export class KydComponent implements OnDestroy {
    @ViewChild('reportsPaginator') reportsPaginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    reports: any[] = [];
    kydSource: any;
    displayedColumns: string[] = ['type', 'createdDate', 'devices', 'actions'];

    // Reports search: free text, a specific date, and an explicit sort-order picker (in
    // addition to still being able to click the "Date de création" column header).
    reportSearchText: string = '';
    reportDateFilter: Date | null = null;
    reportSortOrder: 'desc' | 'asc' = 'desc';

    // A place to see at a glance what the mobile app last pushed for this project's KYDs
    // without opening each tracker individually - editKydTracker() below also lets a new
    // config be prepared for one directly from here (same editor as Ressources > Dispositif).
    kydTrackers: Tracker[] = [];
    kydTrackerFilter: string = '';
    // Checkboxes only show up once explicitly turned on (see toggleMultiSelectMode()) - having
    // them visible at all times pushed a long tracker name onto its own line, right under the
    // absolutely-positioned edit/delete buttons.
    multiSelectMode: boolean = false;

    // inject() rather than a constructor param - needed as a field initializer below, which
    // TypeScript runs before constructor-parameter properties are assigned (a plain
    // `private projectService: ProjectService` param would report "used before its
    // initialization" here). Matches resources.component.ts's own convention for this.
    private readonly projectService: ProjectService = inject(ProjectService);

    // Switching projects from this tab doesn't navigate (see header.component.ts's
    // redirectPaths - this route isn't in it) and this app's custom RouteReuseStrategy
    // wouldn't re-run the constructor even if it did (detach/reattach, not destroy/recreate)
    // - so the only way to notice a project switch here is this effect, reacting directly to
    // the shared signal (same pattern as resources.component.ts/models.component.ts).
    readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;

    private destroy$ = new Subject<void>();

    constructor(
        private reportService: ReportService,
        private trackerService: TrackerService,
        private translate: TranslateService,
        private pop: Pop,
        public dialog: MatDialog,
        public kydConfigTool: TrackerConfigTool
    ) {
        effect(() => {
            const project = this.currentProject();
            if (project?.id) {
                this.getListReports();
                this.getKydTrackers();
                this.getConfigNames();
            }
        });
    }

    // Resolves tracker.config (the template linked to this tracker) into a displayable template
    // name (Devices > gestion des modèles) - global, not project-scoped, but re-fetched alongside
    // the KYD trackers for simplicity; stays live for the same reason getKydTrackers() does.
    private configNameById = new Map<string, string>();

    getConfigNames() {
        this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe((configs: TrackerConfig[]) => {
            this.configNameById = new Map((configs || []).map(c => [c.id, c.name]));
        });
    }

    appliedConfigName(tracker: Tracker): string | null {
        const id = this.kydConfigTool.configIdOf(tracker);
        return id ? (this.configNameById.get(id) ?? null) : null;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Stays open for live socket updates (see getKydTrackers() below, same pattern - the API
    // now broadcasts a Report POST/DELETE the same way it already does for Tracker) - every
    // emission (the initial HTTP snapshot, then any later live one) is the FULL current
    // reports array, so it's reprocessed from scratch each time rather than assuming it only
    // ever runs once.
    getListReports() {
        this.reportService.getReports().pipe(takeUntil(this.destroy$)).subscribe((results) => {
            // A live DELETE can legitimately resolve to a real, empty array (the last report
            // just got removed) - that's fine, show it as-is. But `results` coming back as
            // null/undefined specifically means the socket update's cache lookup missed
            // entirely (see local-storage.service.ts's deleteCollectionItem() - it warns and
            // returns without touching anything when it finds no cached array for the key,
            // which getUpdate() then just passes through as-is) - treating that the same as
            // "zero reports" wiped out the entire table down to "no results" until a full page
            // reload re-primed the cache from scratch. Keep whatever's already showing instead.
            if (results == null) return;

            this.reports = (results || []).map(r => ({
                ...r,
                type: r.type == 'Setting values' ? this.translate.instant('datatable.modify_kyd_values') : this.translate.instant('datatable.import_kyd_events'),
                // createdDate gets overwritten below with a display string - keep the
                // original around so sorting isn't done on formatted text (see
                // sortingDataAccessor below: "05" vs "12" for months sorts wrong as text).
                createdDateRaw: r.createdDate,
                createdDate: format(r.createdDate as string, 'dd/MM/yyyy HH:mm:ss'),
            }));

            // Most recent first by default - sorting the array directly (rather than relying
            // only on MatSort, whose ViewChild may not be resolved yet at this point in a
            // subscribe callback) so the initial render is already in the right order.
            this.reports.sort((a, b) => new Date(b.createdDateRaw as string).getTime() - new Date(a.createdDateRaw as string).getTime());

            if (!this.kydSource) {
                this.kydSource = new MatTableDataSource(this.reports);
                this.kydSource.sortingDataAccessor = (item: any, property: string) =>
                    property === 'createdDate' ? new Date(item.createdDateRaw).getTime() : item[property];
                // Combines the free-text search and the specific-date filter into a single
                // MatTableDataSource filter (which only supports one string criterion at a
                // time) - see applyCombinedFilter() below, which encodes both into that string.
                this.kydSource.filterPredicate = (data: any, filter: string) => {
                    const criteria = JSON.parse(filter);
                    const matchesText = !criteria.text || JSON.stringify(data).toLowerCase().includes(criteria.text);
                    const matchesDate = !criteria.date || isSameDay(new Date(data.createdDateRaw), new Date(criteria.date));
                    return matchesText && matchesDate;
                };
                this.kydSource.sort = this.sort;
                this.kydSource.paginator = this.reportsPaginator;

                if (this.sort) {
                    this.sort.active = 'createdDate';
                    this.sort.direction = 'desc';
                    this.sort.disableClear = true;
                }
            } else {
                // A later (live) emission - the source/sort/paginator/filterPredicate wiring
                // above only needs to happen once, same as onSortOrderChange()'s own update.
                this.kydSource.data = this.reports;
            }

            this.applyCombinedFilter();
        }, err => console.error(err))
    }

    // getTrackers() is project-scoped already (same mechanism the Resources tab relies on)
    // and stays open for live socket updates - takeUntil() tears it down with the component.
    getKydTrackers() {
        this.trackerService.getTrackers().pipe(takeUntil(this.destroy$)).subscribe((trackers: Tracker[]) => {
            this.kydTrackers = (trackers || []).filter(t => t.type == 'KYD' || t.settings?.type == 'KYD');
        }, err => console.error(err))
    }

    get filteredKydTrackers(): Tracker[] {
        const filter = this.kydTrackerFilter.trim().toLowerCase();
        if (!filter) return this.kydTrackers;

        return this.kydTrackers.filter(t =>
            t.name?.toLowerCase().includes(filter) || t.serial?.toLowerCase().includes(filter)
        );
    }

    get selectedKydTrackers(): Tracker[] {
        return this.kydTrackers.filter(t => t.selected);
    }

    // Turning it off (either manually or once a bulk action completes, see bulkConfigureKyd()/
    // bulkDeleteKyd()) also drops whatever was selected - re-enabling starts a fresh selection
    // rather than resuming a stale one from a previous round.
    toggleMultiSelectMode() {
        this.multiSelectMode = !this.multiSelectMode;
        if (!this.multiSelectMode) this.clearKydSelection();
    }

    clearKydSelection() {
        this.kydTrackers.forEach(t => t.selected = false);
    }

    // Operates on filteredKydTrackers (the currently visible/filtered list, see the Filter
    // field above), not every KYD regardless of the active search - selecting devices the user
    // can't currently see would leave them wondering what actually got picked. Mirrors
    // unifield-mobile-2's own devices.page.ts toggleSelectAllKyd().
    get allVisibleKydSelected(): boolean {
        return this.filteredKydTrackers.length > 0 && this.filteredKydTrackers.every(t => t.selected);
    }

    toggleSelectAllKyd() {
        const selectAll = !this.allVisibleKydSelected;
        this.filteredKydTrackers.forEach(t => t.selected = selectAll);
    }

    endMultiSelect() {
        this.multiSelectMode = false;
        this.clearKydSelection();
    }

    // Lets one or several KYD be created straight from this tab - the site itself can't reach
    // out over Bluetooth (see kyd-bulk-add-dialog.component.ts), so this only ever registers
    // the tracker record; a technician still has to add the physical device from the mobile
    // app before it can actually connect. `type: 'KYD'` is set right away, so it shows up
    // here as soon as getKydTrackers()'s live subscription picks up the creation - but with no
    // settings at all yet (see the "not configured" placeholder), so promptConfigureNewKyds()
    // immediately offers to fix that instead of leaving it for the user to notice later.
    openAddKydDialog() {
        const dialogRef = this.dialog.open(KydBulkAddDialogComponent, {
            width: '700px',
            maxWidth: '90vw',
            data: { currentProject: this.currentProject() },
            restoreFocus: false
        });

        dialogRef.afterClosed().subscribe((result: KydBulkAddResult | false | undefined) => {
            if (!result || !result.created.length) return;
            this.promptConfigureNewKyds(result.created);
        });
    }

    // A single new KYD goes straight to its own editor - nothing to choose between. Several at
    // once first asks whether they should all get the same values or be configured one by one.
    private promptConfigureNewKyds(created: Tracker[]) {
        if (created.length === 1) {
            this.editKydTracker(created[0]!);
            return;
        }

        const choiceRef = this.dialog.open(KydConfigureChoiceDialogComponent, {
            width: '450px',
            data: { count: created.length },
            restoreFocus: false
        });

        choiceRef.afterClosed().subscribe(choice => {
            if (choice === 'same') this.bulkConfigureNewKyds(created);
            else if (choice === 'each') this.configureKydsSequentially([...created]);
        });
    }

    // Same dialog/logic as the "configure selection" bulk action - just aimed at the KYDs that
    // were just created rather than whatever happens to be checked in the list.
    private bulkConfigureNewKyds(trackers: Tracker[]) {
        this.dialog.open(KydBulkConfigDialogComponent, {
            width: '600px',
            data: { trackers },
            restoreFocus: false
        });
    }

    // One tracker at a time, in the exact same single-tracker editor used everywhere else on
    // this tab (editKydTracker()) - saving advances to the next tracker in the queue; closing
    // without saving (Cancel, backdrop, Escape - afterClosed() resolves falsy either way) stops
    // here, leaving whatever hasn't been reached yet unconfigured for later.
    private configureKydsSequentially(queue: Tracker[]) {
        const next = queue.shift();
        if (!next) return;

        const dialogRef = this.dialog.open(KydConfigDialogComponent, {
            width: '600px',
            data: { tracker: next },
            restoreFocus: false
        });

        dialogRef.afterClosed().subscribe((saved: boolean) => {
            if (saved) this.configureKydsSequentially(queue);
        });
    }

    // Web equivalent of the mobile app's "configuration multiple" (list-update-kyds.page.ts) -
    // one set of values, edited once, saved into the settings of every selected KYD. Same
    // same-config-for-all vs one-by-one choice as promptConfigureNewKyds() - was skipping
    // straight to the shared-config dialog, with no way to configure the selection one by one.
    bulkConfigureKyd() {
        const trackers = this.selectedKydTrackers;
        if (!trackers.length) return;

        if (trackers.length === 1) {
            this.endMultiSelect();
            this.editKydTracker(trackers[0]!);
            return;
        }

        const choiceRef = this.dialog.open(KydConfigureChoiceDialogComponent, {
            width: '450px',
            data: { count: trackers.length, context: 'edit' },
            restoreFocus: false
        });

        choiceRef.afterClosed().subscribe(choice => {
            this.endMultiSelect();
            if (choice === 'same') this.bulkConfigureNewKyds(trackers);
            else if (choice === 'each') this.configureKydsSequentially([...trackers]);
        });
    }

    // Direct counterpart of bulkConfigureKyd()'s "même configuration pour tous" - skips the
    // editable form entirely: pick an existing template, apply its values as-is to every
    // selected KYD immediately. Distinct from bulkConfigureNewKyds()/KydBulkConfigDialogComponent
    // (template only prefills a form there, still reviewable/editable before applying).
    bulkApplyTemplateDirectly() {
        const trackers = this.selectedKydTrackers;
        if (!trackers.length) return;

        this.dialog.open(TemplatePickerDialogComponent, {
            width: '500px',
            data: { typeFilter: 'KYD' },
            restoreFocus: false
        }).afterClosed().subscribe((template: any) => {
            if (!template) return;

            this.pop.confirm(this.translate.instant('trackers.apply_template_confirm', { name: template.name, count: trackers.length }), (confirm: any) => {
                if (!confirm.ok) return;

                const applies = trackers.map(tracker =>
                    this.kydConfigTool.saveSettings(tracker, template.settings, template.offlineDelay, template.id).pipe(
                        map(() => true),
                        catchError(err => { console.error('[KYD bulk apply template] failed for', tracker.name, err); return of(false); })
                    )
                );

                forkJoin(applies).subscribe(results => {
                    const failedCount = results.filter(r => !r).length;

                    if (failedCount === 0) {
                        this.pop.toast(this.translate.instant('units.device.bulk_config_saved', { count: results.length }), 'success', 4000, 'save');
                    } else if (failedCount < results.length) {
                        this.pop.warning(this.translate.instant('trackers.error.some_kyd_config_failed', { failed: failedCount, total: results.length }));
                    } else {
                        this.pop.error(this.translate.instant('trackers.error.config_save'));
                    }
                    this.endMultiSelect();
                });
            });
        });
    }

    // Same uuid-based batch delete already used by devices.component.ts's own grouped
    // "Supprimer" action, scoped to whatever is currently selected here.
    bulkDeleteKyd() {
        const trackers = this.selectedKydTrackers;
        if (!trackers.length) return;

        this.pop.confirm(this.translate.instant('trackers.confirm_delete_trackers'), (confirm: any) => {
            if (!confirm.ok) return;

            this.trackerService.deleteTrackers({ uuids: trackers.map(t => t.uuid) }).subscribe({
                next: () => {
                    this.pop.toast(this.translate.instant('trackers.success.removed_trackers'), 'success', 4000, 'delete');
                    const deletedIds = new Set(trackers.map(t => t.id));
                    this.kydTrackers = this.kydTrackers.filter(t => !deletedIds.has(t.id));
                    this.endMultiSelect();
                },
                error: (err: any) => {
                    if (err == 'offline') {
                        this.pop.warning(this.translate.instant('units.impossible_you_are_offline'));
                    } else {
                        this.pop.error(this.translate.instant('trackers.error.removing_trackers'));
                    }
                    console.error('[KYD] failed to bulk delete', err);
                }
            });
        });
    }

    hasKydSettings(tracker: Tracker): boolean {
        return !!tracker.settings?.distanceSettings;
    }

    hasKydFriendSettings(tracker: Tracker): boolean {
        return !!tracker.settings?.friendSettings;
    }

    // --- Display values, read from tracker.settings (centimeters, converted to meters here).
    displayUserMode(tracker: Tracker): string | undefined {
        return tracker.settings?.userMode;
    }

    displayMinDistance(tracker: Tracker): number | null {
        const cm = tracker.settings?.distanceSettings?.minDistance;
        return cm != null ? cm / 100 : null;
    }

    displayMaxDistance(tracker: Tracker): number | null {
        const cm = tracker.settings?.distanceSettings?.maxDistance;
        return cm != null ? cm / 100 : null;
    }

    displayUnfriendDistance(tracker: Tracker): number | null {
        const cm = tracker.settings?.friendSettings?.unfriendDistance;
        return cm != null ? cm / 100 : null;
    }

    // Shares a single BLE slot with unfriendDistance on the mobile app's side - only meaningful
    // while friendGroup===99 (exclusion mode), see unifield-mobile-2's ble-detail.page.ts
    // updateValues() for the full explanation.
    displayExclusionDistance(tracker: Tracker): number | null {
        const cm = tracker.settings?.friendSettings?.exclusionDistance;
        return cm != null ? cm / 100 : null;
    }

    // Same separator everywhere a distance is shown on site (see tracker-config-form.component.ts's
    // editable fields) - matching the site's own language rather than plain toFixed(2), which
    // always shows a period regardless of language.
    formatDistance(value: number | null): string {
        return formatDistanceMeters(value, this.translate.currentLang);
    }

    displayFriendGroup(tracker: Tracker): number | null {
        return tracker.settings?.friendSettings?.friendGroupNumber ?? null;
    }

    displayIgnoreGroup(tracker: Tracker): number | null {
        return tracker.settings?.friendSettings?.ignoreGroupNumber ?? null;
    }

    // Opens the same KYD config editor as Ressources > Dispositif (TrackerConfigFormComponent
    // + TrackerConfigTool), in a dialog rather than a sidepanel - lets a config be prepared for a
    // KYD straight from this tab without having to go find it on the Ressources page. No
    // explicit refresh needed on close: setTracker()'s backend PUT broadcasts a socket update
    // that getKydTrackers()'s live subscription already picks up.
    editKydTracker(tracker: Tracker) {
        // A connection attempt from the mobile app is about to (or just did) read/write this
        // tracker's real settings over BLE - opening the editor here at the same moment risks
        // a save landing right as the device's own confirmed state comes back and overwrites it.
        if (this.kydConfigTool.isConnecting(tracker)) {
            this.pop.warning(this.translate.instant('trackers.connecting_blocks_edit'));
            return;
        }

        this.dialog.open(KydConfigDialogComponent, {
            width: '600px',
            data: { tracker },
            restoreFocus: false
        });
    }

    // Lets a KYD be removed straight from this tab instead of having to go find it on the
    // main Devices page first - same dialog/service call as resources.component.ts's own
    // single-tracker delete. kydTrackers is filtered here for instant feedback, but would
    // converge to the same result anyway once getKydTrackers()'s own live socket update for
    // this deletion comes back through.
    deleteKydTracker(tracker: Tracker) {
        const dialogRef = this.dialog.open(DeleteModalComponent, {
            data: { title: this.translate.instant('trackers.title'), name: tracker.name }
        });

        dialogRef.afterClosed().subscribe((res) => {
            if (res !== true) return;

            this.trackerService.deleteTracker(tracker.id).pipe(first()).subscribe({
                next: (result: any) => {
                    const resultKeys = result ? result.map((keyValue: {}) => Object.keys(keyValue)[0]) : [];
                    if (resultKeys.indexOf('success') === -1) {
                        console.error('[KYD] failed to remove tracker', result);
                        return;
                    }
                    this.kydTrackers = this.kydTrackers.filter(t => t.id !== tracker.id);
                },
                error: (err) => console.error(err)
            });
        });
    }

    applyCombinedFilter() {
        this.kydSource.filter = JSON.stringify({
            text: this.reportSearchText.trim().toLowerCase(),
            date: this.reportDateFilter ? this.reportDateFilter.toISOString() : null
        });

        if (this.kydSource.paginator) {
            this.kydSource.paginator.firstPage();
        }
    }

    clearDateFilter() {
        this.reportDateFilter = null;
        this.applyCombinedFilter();
    }

    // Explicit alternative to clicking the "Date de création" column header. Re-sorts the
    // underlying array directly rather than going through MatSort's sortChange (same
    // reasoning as getListReports()'s initial sort): the @ViewChild(MatSort) reference isn't
    // guaranteed to be resolved yet when kydSource.sort gets assigned in that constructor-
    // time subscribe callback, so MatTableDataSource's subscription to it can end up never
    // wired at all - emitting on it then does nothing, which is exactly why this control
    // silently had no effect.
    onSortOrderChange(order: 'desc' | 'asc') {
        this.reportSortOrder = order;
        const factor = order === 'desc' ? -1 : 1;
        this.reports.sort((a, b) => factor * (new Date(a.createdDateRaw).getTime() - new Date(b.createdDateRaw).getTime()));
        this.kydSource.data = this.reports;

        if (this.sort) {
            this.sort.active = 'createdDate';
            this.sort.direction = order;
        }
    }

    viewReport(report: any) {
        this.dialog.open(ReportDetailDialogComponent, {
            width: '600px',
            data: { report }
        });
    }

    deleteReport(report: any) {
        const dialogRef = this.dialog.open(DeleteModalComponent, {
            data: { title: report.type, name: report.createdDate }
        });

        dialogRef.afterClosed().subscribe((res) => {
            if (res !== true) return;

            this.reportService.removeReport(report.id).pipe(first()).subscribe({
                next: () => {
                    this.reports = this.reports.filter(r => r.id !== report.id);
                    this.kydSource.data = this.reports;
                },
                error: (err) => console.error(err)
            });
        });
    }
}
